// Components==============
import Modal from 'components/UI/Modal/Modal';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { createContext, useContext, useState } from 'react';
import Buttons from './components/Buttons/Buttons';
import Checkboxes from './components/Checkboxes/Checkboxes';
import Top from './components/Top/Top';
import Transfer from './components/Transfer/Transfer';
import Users from './components/Users/Users';
import { UserLogicMounter } from './components/useSelectedUserLogic';
import './DeleteModal.module.scss';
// =========================

export type SelectedUserType = { user: UserEntity; memberId: number };

type AllUsersType = {
  user: UserEntity;
  userId: number | undefined;
  name: string;
  memberId: number;
  projectId: number;
}[];

type DeleteModalContextType = {
  selectedUsers: SelectedUserType[];
  setSelectedUsers: React.Dispatch<React.SetStateAction<SelectedUserType[]>>;
  setTransferUser: React.Dispatch<React.SetStateAction<UserEntity | undefined>>;
  themeIsChecked: boolean;
  setThemeIsChecked: React.Dispatch<React.SetStateAction<boolean>>;
  processIsChecked: boolean;
  setProcessIsChecked: React.Dispatch<React.SetStateAction<boolean>>;
  transferUser?: UserEntity;
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  shouldTransfer: boolean;
  userSelectedSelf: boolean;
  allUsers: AllUsersType;
  ownerOfPublicTheme: boolean;
  transferableUsers?: UserEntity[];
  canDelete: boolean;
  userShouldLeaveGroup: boolean;
};

export const DeleteModalContext = createContext({} as DeleteModalContextType);

export default function DeleteModal({
  setModalIsOpen,
}: {
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const [selectedUsers, setSelectedUsers] = useState<SelectedUserType[]>([]);
  const [transferUser, setTransferUser] = useState<undefined | UserEntity>(
    undefined
  );

  const { theme, rootProject, project, studentList, hasStudents } = useContext(
    ProjectDetailsContext
  );

  const user = useRedux((state) => state.authentication?.user);
  const userId = user?.id;

  const userMembership = (rootProject || project)?.members.find(
    (m) => m.user_id === userId
  )?.membership;

  const userSelectedSelf = !!selectedUsers.find(
    (su) => su?.user?.id === userId
  );

  const translate = useTranslate();

  const ownerOfPublicTheme = !!(theme?.created_by === userId && theme?.public);

  const isTeacher = usePermission(['Docent', 'super-admin']);

  const [themeIsChecked, setThemeIsChecked] = useState(false);
  const [processIsChecked, setProcessIsChecked] = useState(false);

  const transferableUsers = rootProject?.members
    .filter((m) => {
      if (isTeacher)
        return m.membership === 'admin' || m.membership === 'teacher';

      return m.membership === 'member' || m.membership === 'admin';
    })
    .map((m) => m.user);

  const shouldTransfer = !!(
    userMembership === 'owner' && transferableUsers?.length
  );

  // Disable theme & process buttons if teacher is in process with students
  const canDelete = userMembership === 'owner';

  const allUsers = studentList
    .filter((s) =>
      canDelete ? s.name !== translate('project.terms.my-process') : true
    )
    .map((s) =>
      s.name === translate('project.terms.my-process')
        ? // Grab user & userId to be display myProcess avatar correctly
          { ...s, user: user as UserEntity, userId }
        : s
    );

  const conditionalProject = rootProject || project;

  const memberLength =
    conditionalProject?.members.filter(
      (m) => m.membership !== 'teacher' && m.membership !== 'invited'
    ).length || 0;

  const studentInProjectWithOnlyTeachers = memberLength === 1 && !isTeacher;
  const hasTeacherMembership = userMembership === 'teacher';
  const hasStudentMembers = memberLength > 1;

  const userShouldLeaveGroup =
    !hasStudents &&
    !studentInProjectWithOnlyTeachers &&
    (hasTeacherMembership || hasStudentMembers);

  return (
    <Modal setModalIsOpen={setModalIsOpen} size="medium">
      <DeleteModalContext.Provider
        value={{
          selectedUsers,
          setSelectedUsers,
          setTransferUser,
          themeIsChecked,
          setThemeIsChecked,
          processIsChecked,
          setProcessIsChecked,
          transferUser,
          setModalIsOpen,
          shouldTransfer,
          userSelectedSelf,
          allUsers,
          ownerOfPublicTheme,
          transferableUsers,
          canDelete,
          userShouldLeaveGroup,
        }}
      >
        <div styleName="wrapper">
          <Top />
          {shouldTransfer ? <Transfer /> : canDelete ? <Checkboxes /> : <></>}
          {isTeacher && !!allUsers.length && <Users />}
        </div>
        <Buttons />
        <UserLogicMounter />
      </DeleteModalContext.Provider>
    </Modal>
  );
}
